import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class DateService {
  constructor() {}

  parseToDate(value: any): Date | null {
    if (!value) {
      return null;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return null;
      }

      const isoDateTimeMatch = /^([0-9]{4})-([0-9]{2})-([0-9]{2})T/.exec(trimmed);
      if (isoDateTimeMatch) {
        const year = Number(isoDateTimeMatch[1]);
        const month = Number(isoDateTimeMatch[2]);
        const day = Number(isoDateTimeMatch[3]);
        const date = new Date(year, month - 1, day);
        if (
          date.getFullYear() === year &&
          date.getMonth() === month - 1 &&
          date.getDate() === day
        ) {
          date.setHours(0, 0, 0, 0);
          return date;
        }
        return null;
      }

      const isoDateOnlyMatch = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/.exec(trimmed);
      if (isoDateOnlyMatch) {
        const year = Number(isoDateOnlyMatch[1]);
        const month = Number(isoDateOnlyMatch[2]);
        const day = Number(isoDateOnlyMatch[3]);
        const date = new Date(year, month - 1, day);
        if (
          date.getFullYear() === year &&
          date.getMonth() === month - 1 &&
          date.getDate() === day
        ) {
          date.setHours(0, 0, 0, 0);
          return date;
        }
        return null;
      }

      // Try parsing with various separators: /, -, and .
      const separators = ['/', '-', '\\.'];
      for (const separator of separators) {
        const regex = new RegExp(`^([0-9]{1,2})${separator}([0-9]{1,2})${separator}([0-9]{4})$`);
        const match = regex.exec(trimmed);
        
        if (match) {
          const first = Number(match[1]);
          const second = Number(match[2]);
          const year = Number(match[3]);

          // Determine which is month and which is day
          const month = first > 12 && second <= 12 ? second : first;
          const day = first > 12 && second <= 12 ? first : second;

          const date = new Date(year, month - 1, day);
          if (
            date.getFullYear() === year &&
            date.getMonth() === month - 1 &&
            date.getDate() === day
          ) {
            date.setHours(0, 0, 0, 0);
            return date;
          }
        }
      }

      const fallback = new Date(trimmed);
      if (!isNaN(fallback.getTime())) {
        fallback.setHours(0, 0, 0, 0);
        return fallback;
      }
    }

    if (typeof value === 'number' || value instanceof Date) {
      const date = new Date(value);
      if (isNaN(date.getTime())) {
        return null;
      }
      date.setHours(0, 0, 0, 0);
      return date;
    }

    return null;
  }

  formatToDateString(value: any): string {
    const date = this.parseToDate(value);
    if (!date) {
      return '';
    }

    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    return `${year}-${this.to2digit(month)}-${this.to2digit(day)}`;
  }

  prepareForSave(date: Date | string | null): string {
    return this.formatToDateString(date);
  }

  durationInDays(startDate: Date | string | null, endDate: Date | string | null): number {
    const start = this.parseToDate(startDate);
    const end = this.parseToDate(endDate);

    if (!start || !end) {
      return 0;
    }

    const utcStart = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());
    const utcEnd = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());
    const diffTime = utcEnd - utcStart;
    return Math.round(diffTime / (1000 * 60 * 60 * 24)) + 1;
  }

  addDays(date: Date | string | null, days: number): Date | null {
    const parsedDate = this.parseToDate(date);
    if (!parsedDate) {
      return null;
    }

    const result = new Date(parsedDate);
    result.setDate(result.getDate() + days);
    result.setHours(0, 0, 0, 0);
    return result;
  }

  getPastYear(years: number): Date {
    const pastDate = new Date(this.todayDate());
    pastDate.setFullYear(pastDate.getFullYear() - years);
    return pastDate;
  }

  getPastDate(days: number = 0): Date {
    return this.addDays(this.todayDate(), -days) as Date;
  }

  isDateInPast(value: any): boolean {
    const targetDate = this.parseToDate(value);
    if (!targetDate) {
      return false;
    }
    const currentDate = new Date();
    currentDate.setHours(23, 59, 59, 999);

    return targetDate < currentDate;
  }

  isDateInRange(
    dateToCheck: any,
    startDate: any,
    endDate: any,
    inclusive: boolean = true,
  ): boolean {
    const parsedDate = this.parseToDate(dateToCheck);
    const parsedStartDate = this.parseToDate(startDate);
    const parsedEndDate = this.parseToDate(endDate);

    if (!parsedDate || !parsedStartDate || !parsedEndDate) {
      return false;
    }

    // Create end of day version of end date for inclusive range
    const endDateWithTime = this.parseToDate(parsedEndDate) as Date;
    if (inclusive) {
      endDateWithTime.setHours(23, 59, 59, 999);
    }

    return parsedDate >= parsedStartDate && parsedDate <= endDateWithTime;
  }

  todayDate(): Date {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }

  private to2digit(n: number): string {
    return n < 10 ? `0${n}` : `${n}`;
  }
}
